import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const ControlsPTDetentionCodeWise = ({
  data,
  setFilteredData,
  openStates,
  setOpenStates,
}) => {
  const [searchValues, setSearchValues] = useState({
    Year: "",
    SECTION: "",
    DET_CODE: "",
    SUB_CODE: "",
    TRAIN_NO: "",
  });

  const [selectedFY, setSelectedFY] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState(new Set());
  const [selectedDetCode, setSelectedDetCode] = useState(new Set());
  const [selectedSubCode, setSelectedSubCode] = useState(new Set());
  const [selectedTrainNo, setSelectedTrainNo] = useState(new Set());

  const toggleSelection = (setFunc, currentSet, value) => {
    const newSet = new Set(currentSet);
    newSet.has(value) ? newSet.delete(value) : newSet.add(value);
    setFunc(newSet);
  };

  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  const availableFY = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [...new Set(data.map((d) => d.financial_year))].sort();
  }, [data]);

  const availableSection = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [
      ...new Set(
        data
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) =>
            (entry.children || []).map((group) => group.SECTION)
          )
      ),
    ].sort();
  }, [data, selectedFY]);

  const availableDetCode = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [
      ...new Set(
        data
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedSection.has(group.SECTION))
          .flatMap((group) =>
            (group.children || []).map((subGroup) => subGroup.DET_CODE)
          )
      ),
    ];
  }, [data, selectedFY, selectedSection]);

  const availableSubCode = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [
      ...new Set(
        data
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedSection.has(group.SECTION))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedDetCode.has(subGroup.DET_CODE))
          .flatMap((subGroup) =>
            (subGroup.children || []).map((comm) => comm.SUB_CODE)
          )
      ),
    ];
  }, [data, selectedFY, selectedSection, selectedDetCode]);

  const availableTrainNo = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return [
      ...new Set(
        data
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) => entry.children || [])
          .filter((group) => selectedSection.has(group.SECTION))
          .flatMap((group) => group.children || [])
          .filter((subGroup) => selectedDetCode.has(subGroup.DET_CODE))
          .flatMap((subGroup) => subGroup.children || [])
          .filter((comm) => selectedSubCode.has(comm.SUB_CODE))
          .flatMap((comm) => (comm.data || []).map((item) => item.TRAIN_NO))
      ),
    ].sort();
  }, [data, selectedFY, selectedSection, selectedDetCode, selectedSubCode]);

  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data
      .filter((entry) => selectedFY.has(entry.financial_year))
      .map((entry) => {
        const filteredChildren = (entry.children || [])
          .filter((group) => selectedSection.has(group.SECTION))
          .map((group) => {
            const filteredGroupChildren = (group.children || [])
              .filter((subGroup) => selectedDetCode.has(subGroup.DET_CODE))
              .map((subGroup) => {
                const filteredSubChildren = (subGroup.children || [])
                  .filter((comm) => selectedSubCode.has(comm.SUB_CODE))
                  .map((comm) => {
                    const filteredData = (comm.data || []).filter((item) =>
                      selectedTrainNo.has(item.TRAIN_NO)
                    );
                    return { ...comm, data: filteredData };
                  })
                  .filter((comm) => comm.data.length > 0);
                return { ...subGroup, children: filteredSubChildren };
              })
              .filter((subGroup) => subGroup.children.length > 0);
            return { ...group, children: filteredGroupChildren };
          })
          .filter((group) => group.children.length > 0);
        return { ...entry, children: filteredChildren };
      })
      .filter((entry) => entry.children.length > 0);
  }, [
    data,
    selectedFY,
    selectedSection,
    selectedDetCode,
    selectedSubCode,
    selectedTrainNo,
  ]);

  const defaultSetup = () => {
    if (!data || !Array.isArray(data)) return;

    const allFY = new Set(data.map((d) => d.financial_year));
    const allSections = new Set(
      data.flatMap((d) => (d.children || []).map((group) => group.SECTION))
    );
    const allDetCodes = new Set(
      data
        .flatMap((d) => d.children || [])
        .flatMap((group) =>
          (group.children || []).map((subGroup) => subGroup.DET_CODE)
        )
    );
    const allSubCodes = new Set(
      data
        .flatMap((d) => d.children || [])
        .flatMap((group) =>
          (group.children || []).flatMap((subGroup) =>
            (subGroup.children || []).map((comm) => comm.SUB_CODE)
          )
        )
    );
    const allTrainNos = new Set(
      data
        .flatMap((d) => d.children || [])
        .flatMap((group) =>
          (group.children || []).flatMap((subGroup) =>
            (subGroup.children || []).flatMap((comm) =>
              (comm.data || []).map((item) => item.TRAIN_NO)
            )
          )
        )
    );

    setSelectedFY(allFY);
    setSelectedSection(allSections);
    setSelectedDetCode(allDetCodes);
    setSelectedSubCode(allSubCodes);
    setSelectedTrainNo(allTrainNos);
    setSearchValues({
      Year: "",
      SECTION: "",
      DET_CODE: "",
      SUB_CODE: "",
      TRAIN_NO: "",
    });
    setOpenStates({
      Year: false,
      SECTION: false,
      DET_CODE: false,
      SUB_CODE: false,
      TRAIN_NO: false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  useEffect(() => {
    setFilteredData(filteredData);
  }, [filteredData, setFilteredData]);

  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const filteredItems = items.filter((item) =>
      item?.toString().toLowerCase().includes(searchValues[title].toLowerCase())
    );

    return (
      <div
        className="col-md mt-2 p-1"
        style={{ minWidth: title === "SECTION" ? "200px" : "150px" }}
      >
        <div
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="newUiBody"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title.replace("_", " ").toLowerCase()}
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="newUiBody"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10, // High z-index for dropdowns
              }}
            >
              <strong>{title.replace("_", " ")}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title
                  .replace("_", " ")
                  .toLowerCase()}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title]}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className={`col-md-${
                        title === "SECTION" ? "" : "4"
                      } fade-in`}
                      style={{ fontSize: "12px", zIndex: 10 }} // High z-index for items
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(setFunc, selectedSet, item)
                          }
                        />
                        {item}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              <div className="d-flex justify-content-evenly mt-1">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFunc(new Set(items))}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setFunc(new Set())}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ zIndex: 10 }}>
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          // Removed backdropFilter and WebkitBackdropFilter
        }}
      >
        {renderCheckboxList("Year", availableFY, selectedFY, setSelectedFY)}
        {renderCheckboxList(
          "SECTION",
          availableSection,
          selectedSection,
          setSelectedSection
        )}
        {renderCheckboxList(
          "DET_CODE",
          availableDetCode,
          selectedDetCode,
          setSelectedDetCode
        )}
        {renderCheckboxList(
          "SUB_CODE",
          availableSubCode,
          selectedSubCode,
          setSelectedSubCode
        )}
        {renderCheckboxList(
          "TRAIN_NO",
          availableTrainNo,
          selectedTrainNo,
          setSelectedTrainNo
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-1">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 3px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default ControlsPTDetentionCodeWise;
